import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { projectEnvironmentsTranslationRef } from './translation';

/**
 * How long a pipeline has run, in words — « moins d’une minute », « 3 min »,
 * « 1 h 05 » —, from the whole minutes of `pipelineElapsedMinutes`
 * (pipelines.ts). Shared by the branch page's « Pipelines » tile and the
 * build step of the « being prepared » card, so the two say it alike.
 */
export function useElapsedWords(): (minutes: number) => string {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return minutes => {
    if (minutes < 1) {
      return t('tile.elapsedUnderMinute');
    }
    if (minutes < 60) {
      return t('tile.elapsedMinutes', { minutes: String(minutes) });
    }
    return t('tile.elapsedHours', {
      hours: String(Math.floor(minutes / 60)),
      minutes: String(minutes % 60).padStart(2, '0'),
    });
  };
}

/**
 * How long ago an environment was created, from its whole days
 * (`BranchActivity.ageDays`, branchKind.ts): « aujourd’hui », « hier »,
 * « il y a N j ». Shared by the branch page's « Créé » and the sentence of
 * the ephemeral badge's tooltip and the sidebar rows (`useBranchDescription`),
 * so the three say it alike.
 *
 * Messages rather than `Intl.RelativeTimeFormat` (home/relativeTime.ts):
 * with `numeric: 'auto'` it says « avant-hier » for 2 days, and its English
 * days are written out (« 3 days ago ») where every duration of this plugin
 * reads « N d ».
 */
export function useAgeWords(): (days: number) => string {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return days => {
    if (days === 0) {
      return t('age.today');
    }
    if (days === 1) {
      return t('age.yesterday');
    }
    return t('age.daysAgo', { days: String(days) });
  };
}

/**
 * A duration in words, as short as a pill allows — « < 1 s », « 6 s »,
 * « 2 min 05 s », « 1 h 02 min » —, the format of the project deletion
 * page's pills (`frenchDuration`, taskProjectBanner/deletionTask.ts), here
 * through the plugin's messages. For the « mise en service » of a branch:
 * its steps' pills, the « ready in » of its banner.
 */
export function useDurationWords(): (ms: number) => string {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return ms => {
    const seconds = Math.round(ms / 1000);
    if (seconds < 1) {
      return t('duration.underSecond');
    }
    if (seconds < 60) {
      return t('duration.seconds', { seconds: String(seconds) });
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return t('duration.minutes', {
        minutes: String(minutes),
        seconds: String(seconds % 60).padStart(2, '0'),
      });
    }
    return t('duration.hours', {
      hours: String(Math.floor(minutes / 60)),
      minutes: String(minutes % 60).padStart(2, '0'),
    });
  };
}
